interface DataObject {
  [key: string]: boolean;
}

enum TokenType {
  Identifier,
  Conjunction,
  Parenthesis,
  Comparator,
}

interface Token {
  type: TokenType;
  value: any;
}

interface ASTNode {
  token: Token;
  left?: ASTNode;
  right?: ASTNode;
}

/** Longest wildcard pattern we are willing to turn into a matcher. */
const maxWildcardPatternLength = 512;

/**
 * Builds an anchored matcher for a SQL-style wildcard pattern, where `%` stands
 * for any run of characters and `_` for a single one.
 *
 * The pattern reaches us from metadata and from filter values typed by the
 * user, so every character is escaped before the two wildcards are expanded:
 * without that, the pattern is regex source and an author can change what the
 * comparison means, or hand us one that backtracks catastrophically. Runs of
 * `%` are collapsed and the length is capped for the same reason.
 */
function wildcardToRegExp(pattern: string): RegExp {
  const source = pattern
    .slice(0, maxWildcardPatternLength)
    .replace(/%{2,}/g, "%")
    .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    .replace(/%/g, ".*")
    .replace(/_/g, ".");

  return new RegExp("^" + source + "$");
}

export class ExpressionEvaluator {
  private data: DataObject;
  private expression: string;

  constructor(data?: DataObject, expression?: string) {
    this.data = data ? data : {};
    this.expression = expression ? expression : "";
  }

  evaluate(): boolean {
    const tokens = this.tokenize(this.expression, this.data);
    const ast = this.constructAST(this.shuntingYard(tokens));

    return this.evaluateAST(ast);
  }

  evaluateExpression(expression: string, data: { [key: string]: any }) {
    const tokens = this.tokenize(expression, data);
    const ast = this.constructAST(this.shuntingYard(tokens));

    return this.evaluateAST(ast);
  }

  private tokenize(expression: string, data: { [key: string]: any }): Token[] {
    const tokens: Token[] = [];

    // old method: expression.split(/(\s*(?:\(|\)|&&|\|\||!==|===|!=|==|>|<|>=|<=|\^)\s*)/g)
    this.splitExpression(expression)
      .forEach((param) => {
        let tokenType: TokenType;
        switch (param.trim()) {
          case "":
          case null:
            return;
          case "!==":
          case "===":
          case "!=":
          case "==":
          case ">":
          case "<":
          case ">=":
          case "<=":
            tokenType = TokenType.Comparator;
            break;
          case "&&":
          case "||":
            tokenType = TokenType.Conjunction;
            break;
          case "(":
          case ")":
            tokenType = TokenType.Parenthesis;
            break;
          default:
            tokenType = TokenType.Identifier;
        }

        let value: any;

        if (tokenType == TokenType.Identifier) {
          if (param.charAt(0) === "$") {
            value = parseInt(param.slice(1));
          } else if (
            param.charAt(0) == "'" &&
            param.charAt(param.length - 1) == "'"
          ) {
            value = param.slice(1, -1);
          } else if (param == "null") {
            value = null;
          } else if (param == "undefined") {
            value = undefined;
          } else if (param == "false") {
            value = false;
          } else if (param == "true") {
            value = true;
          } else {
            value = param
              .trim()
              .split(".")
              .reduce((a, b) => a?.[b], data);
          }
        } else {
          value = param;
        }

        tokens.push({
          type: tokenType,
          value,
        });
      });

    return tokens;
  }

  private splitExpression(expression: string): string[] {
    const operators = ['!==', '===', '!=', '==', '>=', '<=', '&&', '||', '(', ')', '>', '<', '^'];
    const expressionParts: string[] = [];
    let currentToken = '';
    let isInsideQuotedString = false;
    let charIndex = 0;

    while (charIndex < expression.length) {
      if (expression[charIndex] === "'") {
        isInsideQuotedString = !isInsideQuotedString;
        currentToken += expression[charIndex++];
        continue;
      }

      if (!isInsideQuotedString) {
        let matchedOperator = '';
        for (const op of operators) {
          if (expression.startsWith(op, charIndex)) {
            matchedOperator = op;
            break;
          }
        }

        if (matchedOperator) {
          const trimmedToken = currentToken.trimEnd();
          if (trimmedToken) {
            expressionParts.push(trimmedToken);
          }
          expressionParts.push(matchedOperator);
          currentToken = '';
          charIndex += matchedOperator.length;
          // skip leading whitespace after operator
          while (charIndex < expression.length && expression[charIndex] === ' ') charIndex++;
          continue;
        }
      }

      currentToken += expression[charIndex++];
    }

    const finalToken = currentToken.trimEnd();
    if (finalToken) {
      expressionParts.push(finalToken);
    }
    return expressionParts;
  }

  private constructAST(tokens: Token[]): ASTNode {
    const stack: ASTNode[] = [];

    for (const token of tokens) {
      if (token.type === TokenType.Identifier) {
        stack.push({ token: token });
      } else if (
        token.type === TokenType.Comparator ||
        token.type === TokenType.Conjunction
      ) {
        const right = stack.pop();
        const left = stack.pop();

        stack.push({ token, left, right });
      }
    }

    if (stack.length !== 1) {
      throw new Error("Invalid expression");
    }

    return stack.pop() as ASTNode;
  }

  private shuntingYard(tokens: Token[]): Token[] {
    const precedence: { [key: string]: number } = {
      "&&": 1,
      "||": 2,
      "!==": 3,
      "===": 3,
      "==": 3,
      "!=": 4,
      "<": 4,
      ">": 4,
      "<=": 4,
      ">=": 4,
    };

    let outputQueue: Token[] = [];
    let operatorStack: Token[] = [];

    while (tokens.length > 0) {
      const token: Token | undefined = tokens.pop();
      if (!token) {
        break;
      }

      switch (token.type) {
        case TokenType.Identifier:
          outputQueue.push(token);
          break;
        case TokenType.Comparator:
        case TokenType.Conjunction:
          while (
            operatorStack.length > 0 &&
            precedence[token.value.trim()] <=
              precedence[operatorStack[operatorStack.length - 1].value.trim()]
          ) {
            outputQueue.push(operatorStack.pop() as Token);
          }
          operatorStack.push(token);
          break;
        case TokenType.Parenthesis:
          operatorStack.push(token);
          while (
            operatorStack.length > 0 &&
            operatorStack[operatorStack.length - 1].type !==
              TokenType.Parenthesis
          ) {
            outputQueue.push(operatorStack.pop() as Token);
          }
          if (operatorStack.length === 0) {
            throw new Error("Mismatched parentheses");
          }
          // Discard the '(' from the stack
          operatorStack.pop();
          break;
        default:
          throw new Error("Unexpected token type: " + JSON.stringify(token));
      }
    }

    while (operatorStack.length > 0) {
      const operator = operatorStack.pop();
      // @ts-ignore
      if (operator.type === TokenType.Parenthesis) {
        throw new Error("Mismatched parentheses");
      }
      // @ts-ignore
      outputQueue.push(operator);
    }

    return outputQueue;
  }

  private evaluateAST(ast: ASTNode): boolean {
    // Traverse the AST and evaluate the expression recursively
    // Example: AST representing "a AND (b OR c)" => Evaluate "a", then "b OR c", then "a AND (b OR c)"
    if (ast.token.type == TokenType.Identifier) {
      return ast.token.value;
    } else if (
      ast.token.type == TokenType.Comparator ||
      ast.token.type == TokenType.Conjunction
    ) {
      // @ts-ignore
      const left: any = this.evaluateAST(ast.left);
      // @ts-ignore
      const right: any = this.evaluateAST(ast.right);

      switch (ast.token.value.trim()) {
        case "&&":
          return left && right;
        case "||":
          return left || right;
        case "==":
          if (typeof left === "string" && typeof right === "string") {
            // Add contains, startsWith and endsWith functionality with % and _ wildcards
            //console.log(left, right);
            if (left.includes("%") || left.includes("_")) {
              return wildcardToRegExp(left).test(right);
            }
            return left.toLowerCase() == right.toLowerCase(); // Loose equality comparison
          }
          return left == right; // Loose equality comparison
        case "!=":
          if (typeof left === "string" && typeof right === "string") {
            // Add contains, startsWith and endsWith functionality with % and _ wildcards
            if (left.includes("%") || left.includes("_")) {
              return !wildcardToRegExp(left).test(right);
            }
            return left.toLowerCase() != right.toLowerCase(); // Loose equality comparison
          }
          return left != right; // Loose equality comparison
        case "===":
          return left === right; // Strict equality comparison
        case "!==":
          return left !== right; // Strict inequality comparison
        default:
          throw new Error("Invalid operator: " + ast.token.value.trim());
      }
    } else {
      throw new Error("Invalid AST node");
    }
  }
}
